import {FC, MouseEvent} from "react";
import {Avatar, Badge, Button, Tooltip, Typography} from "antd";
import {BankOutlined, DeleteOutlined, UndoOutlined} from "@ant-design/icons";
import styled from "styled-components";
import {formatDistanceToNow} from "date-fns";
import {de} from "date-fns/locale";
import {IMessage} from "../../../services/types.ts";
import {colors} from "../../../util/colors.ts";
import {getInitials, STATUS_CONFIG, ApplicationStatus} from "../constants.ts";

const {Text} = Typography;

interface IMessageItemProps {
    item: IMessage;
    selected?: boolean;
    onSelect?: (item: IMessage) => void;
    onToggleHidden?: (item: IMessage) => void;
    toggleHiddenLoading?: boolean;
}

// Derive a UI-only status from the existing IMessage shape so the chip
// is purely cosmetic without touching backend data.
const deriveStatus = (item: IMessage): ApplicationStatus => {
    if (!item._job_demand_with_labels?.[0]?.id) return "rejected";
    return "pending";
};

export const MessageItem: FC<IMessageItemProps> = ({
    item,
    selected,
    onSelect,
    onToggleHidden,
    toggleHiddenLoading,
}) => {
    const employer = item._client?.profile_name || "Unbekannter Arbeitgeber";
    const jobTitle = item._job_demand_with_labels?.[0]?.title
        || "Keine Stelle angehängt oder sie wurde deaktiviert";
    const lastMessage = item.message || "";
    const status = deriveStatus(item);
    const statusCfg = STATUS_CONFIG[status];
    const isHidden = !!item.candidate_hidden;

    const time = item.created_at
        ? formatDistanceToNow(new Date(item.created_at), {addSuffix: true, locale: de})
        : "";

    const handleToggleHidden = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onToggleHidden?.(item);
    };

    return (
        <Row
            $selected={!!selected}
            onClick={() => onSelect?.(item)}
        >
            <AvatarSlot>
                <Badge dot={false} offset={[-4, 4]}>
                    <Avatar
                        size={44}
                        style={{
                            backgroundColor: selected ? colors.main : "#bfbfbf",
                            fontSize: 14,
                            fontWeight: 600,
                        }}
                    >
                        {getInitials(employer)}
                    </Avatar>
                </Badge>
            </AvatarSlot>
            <Body>
                <TopRow>
                    <Text strong style={{fontSize: 14, color: colors.text}} ellipsis>
                        {employer}
                    </Text>
                    <TopRowRight>
                        <Text type="secondary" style={{fontSize: 12, flexShrink: 0}}>
                            {time}
                        </Text>
                        {onToggleHidden && (
                            <Tooltip title={isHidden ? "Wiederherstellen" : "Löschen"}>
                                <HoverIconButton
                                    type="text"
                                    size="small"
                                    danger={!isHidden}
                                    icon={isHidden ? <UndoOutlined/> : <DeleteOutlined/>}
                                    loading={toggleHiddenLoading}
                                    onClick={handleToggleHidden}
                                />
                            </Tooltip>
                        )}
                    </TopRowRight>
                </TopRow>
                <SubRow>
                    <BankOutlined style={{marginRight: 4, fontSize: 11}}/>
                    <Text type="secondary" style={{fontSize: 12}} ellipsis>
                        {jobTitle}
                    </Text>
                </SubRow>
                <Snippet ellipsis>{lastMessage}</Snippet>
                <Chip $bg={statusCfg.bg} $color={statusCfg.color}>{statusCfg.label}</Chip>
            </Body>
        </Row>
    );
};

const Row = styled.div<{$selected: boolean}>`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    background: ${(p) => (p.$selected ? "#f0f0f0" : "#fff")};
    border-bottom: 1px solid #f0f0f0;
    border-left: 3px solid ${(p) => (p.$selected ? colors.main : "transparent")};
    transition: background 0.15s ease;

    &:hover {
        background: ${(p) => (p.$selected ? "#f0f0f0" : "#e6f4ff")};
    }
`;

const TopRowRight = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    margin-left: 8px;
`;

const HoverIconButton = styled(Button)`
    opacity: 0;
    transition: opacity 0.15s ease;

    ${Row}:hover & {
        opacity: 1;
    }

    &.ant-btn-loading {
        opacity: 1;
    }

    @media (max-width: 767px) {
        opacity: 1;
    }
`;

const AvatarSlot = styled.div`
    flex-shrink: 0;
`;

const Body = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubRow = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
`;

const Snippet = styled(Text)`
    && {
        font-size: 13px;
        color: #595959;
        display: block;
    }
`;

const Chip = styled.span<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    align-self: flex-start;
    font-size: 11px;
    line-height: 18px;
    padding: 0 8px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}20;
    white-space: nowrap;
`;

// Kept exports for backwards-compat with cross-package imports.
export const BtnStyledMessageItem = styled(Button)`
    @media (max-width: 767px) {
        width: 100%;
    }
`;

export const ButtonGroupMessageItem = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 8px;

    @media (max-width: 768px) {
        width: calc(100% - 10px);
        flex-direction: column;
        align-items: center;
    }
`;

export const RowItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 900px) {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
        padding: 0;
        border-bottom: none;
    }
`;
